import Title from "common/Typography/Title";
import { HelperTooltip } from "common/Tooltips/HelperTooltip";
import React, { useState } from "react";
import Input from "common/Inputs/Input";
import TextArea from "common/Inputs/TextArea";
import { getReferralUrl } from "features/affiliation/common/utils";
import styled, { css, useTheme } from "styled-components";
import Form from "common/Form/Form";
import { CreateReferralLinkRequest } from "types/affiliation.types";
import { FormWrapper } from "features/affiliation/common/styles";
import {
  getNewLookTokens,
  NEW_LOOK_RADIUS,
  newLookType,
} from "common/NewLook/tokens.ts";
import { AnalyticsPageName, LanguageNameSpaces } from "@shared/types";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";

interface CreateStepProps {
  onSubmit: (values: CreateReferralLinkRequest) => void;
  isEdit?: boolean;
  referralCode?: string;
  description?: string;
}

const CreateOrEditStep = ({
  onSubmit,
  isEdit,
  referralCode,
  description,
}: CreateStepProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.AFFILIATION);
  const { isNewLook } = useTheme();
  const [newReferralCode, setNewReferralCode] = useState<string>();

  return (
    <FormWrapper>
      <Form
        id="createLinkForm"
        name="createLink"
        layout="vertical"
        onFinish={onSubmit}
        requiredMark={false}
        style={{ width: "100%" }}
      >
        <Form.Item
          label={
            <FormItemTitle>
              <Title level={4}>{t("links.referral_code")}</Title>
              <HelperTooltip
                analyticsFieldName="Referral code"
                analyticsPageName={
                  AnalyticsPageName.CREATE_OR_EDIT_REFERRAL_LINK
                }
                description={t("links.referral_code_tooltip")}
              />
            </FormItemTitle>
          }
          name="referral_code"
          rules={[
            { required: true, message: t("links.referral_code_required") },
            {
              pattern: REFERRAL_CODE_REGEX,
              message: t("links.referral_code_pattern"),
            },
          ]}
          initialValue={referralCode}
        >
          <Input
            onChange={(e) => setNewReferralCode(e.target.value)}
            disabled={isEdit}
          />
        </Form.Item>
        <Url>
          <Title level={4} type="secondary">
            {t("links.url_preview")}
          </Title>
          <Title level={4} type="secondary">
            {getReferralUrl(isEdit ? referralCode : newReferralCode)}
          </Title>
        </Url>
        <Form.Item
          label={
            <FormItemTitle>
              <Title level={4}>{t("links.description_label")}</Title>
              <HelperTooltip
                analyticsFieldName="Description"
                analyticsPageName={
                  AnalyticsPageName.CREATE_OR_EDIT_REFERRAL_LINK
                }
                description={t("links.description_tooltip")}
              />
            </FormItemTitle>
          }
          name="description"
          initialValue={description}
        >
          <DescriptionTextArea
            // New look: a regular-size field, like the referral code input.
            size={isNewLook ? undefined : "large"}
            rows={4}
            autoSize={{ minRows: 3 }}
            showCount
            maxLength={100}
          />
        </Form.Item>
      </Form>
    </FormWrapper>
  );
};
export default CreateOrEditStep;

const REFERRAL_CODE_REGEX = /^[A-Za-z0-9]*$/;

const FormItemTitle = styled.div`
  display: flex;
  ${(p) =>
    p.theme.isNewLook &&
    css`
      align-items: center;
      gap: 2px;
      /* The label role, like every new-look form label. */
      h4 {
        margin: 0;
        ${newLookType.label};
        color: ${getNewLookTokens(p.theme).secondary};
      }
    `}
`;
const DescriptionTextArea = styled(TextArea)`
  ${(p) =>
    p.theme.isNewLook &&
    css`
      /* The field's rounded corners stay visible around the text area, and
         the counter sits in the field's bottom corner (not over whatever
         follows the field). */
      &.ant-input-textarea-affix-wrapper > textarea.ant-input {
        padding-bottom: 28px;
        border-radius: inherit;
      }
      .ant-input-data-count {
        bottom: 6px;
        inset-inline-end: 12px;
        color: ${p.theme.colors.secondaryText};
        font-size: 12px;
        line-height: 18px;
        pointer-events: none;
      }
    `}
`;
const Url = styled.div`
  margin-bottom: 16px;
  ${(p) =>
    p.theme.isNewLook &&
    css`
      display: flex;
      align-items: baseline;
      gap: 8px;
      /* A hint of the code field above it: 8px under the field, 16px (the
         field rhythm) before the next field. */
      margin: -8px 0 16px;
      padding: 8px 12px;
      border-radius: ${NEW_LOOK_RADIUS.control}px;
      background: ${getNewLookTokens(p.theme).well};
      box-shadow: inset 0 0 0 1px ${getNewLookTokens(p.theme).hairline};
      h4 {
        margin: 0;
        ${newLookType.meta};
        color: ${getNewLookTokens(p.theme).muted};
        white-space: nowrap;
      }
      h4:last-child {
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        color: ${getNewLookTokens(p.theme).text};
      }
    `}
`;
